import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Language data
const translations = {
  en: {
    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.children': 'Children',
    'nav.activities': 'Activities',
    'nav.calendar': 'Calendar',
    'nav.chores': 'Chores',
    'nav.tasks': 'Tasks',
    'nav.grocery': 'Grocery',
    'nav.coparents': 'Co-Parents',
    'nav.settings': 'Settings',
    'nav.profile': 'Profile',
    'nav.about': 'About',
    'nav.logout': 'Logout',
    'nav.adminPanel': 'Admin Panel',
    
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Dashboard',
    'dashboard.loading': 'Loading your dashboard...',
    'dashboard.todayOverview': "Today's Overview",
    'dashboard.completed': 'Completed',
    'dashboard.upcoming': 'Upcoming',
    'dashboard.noActivities': 'No activities scheduled for today.',
    'dashboard.hasActivities': 'You have {count} {count, plural, =1 {activity} other {activities}} today.',
    'dashboard.addActivity': 'Add Activity',
    'dashboard.children': 'Children',
    'dashboard.viewAll': 'View All',
    'dashboard.viewCalendar': 'View Calendar',
    'dashboard.noChildren': 'No children added yet.',
    'dashboard.addChild': 'Add Child',
    'dashboard.todayActivities': "Today's Activities",
    'dashboard.upcomingActivities': 'Upcoming Activities',
    'dashboard.noActivitiesToday': 'No activities scheduled for today',
    'dashboard.noUpcomingActivities': 'No upcoming activities in the next 7 days',
    'dashboard.scheduleActivity': 'Schedule Activity',
    'dashboard.offline': 'You are offline',
    'dashboard.online': 'You are online',
    'dashboard.schedulingConflicts': 'Scheduling Conflicts',
    
    // Activity Status
    'activity.completed': 'Completed',
    'activity.inProgress': 'In Progress',
    'activity.comingUp': 'Coming Up',
    'activity.planned': 'Planned',
    
    // Notifications
    'notification.offline': "You're offline",
    'notification.offlineMessage': 'Showing cached data. Changes will sync when you\'re back online.',
    'notification.enableNotifications': 'Enable Notifications',
    'notification.notificationMessage': 'Get reminders for upcoming activities and schedule alerts.',
    'notification.enable': 'Enable',
    'notification.enabling': 'Enabling...',
    'notification.enabled': 'Notifications enabled successfully!',
    'notification.denied': 'Notification permission was denied. You can enable it later in your browser settings.',
    'notification.failed': 'Failed to enable notifications. Please try again or check your browser settings.',
    'notification.conflictDetected': 'Scheduling Conflict Detected',
    'notification.upcomingActivity': 'Upcoming Activity',
    'notification.online': 'Online',
    'notification.offlineStatus': 'Offline',
    'notification.retry': 'Tap to retry connection',
    'notification.connected': 'You are online and connected',
    'notification.reconnecting': 'Attempting to reconnect...',
    
    // Error Messages
    'error.something_wrong': 'Oops! Something went wrong',
    'error.refresh': 'Refresh Page',
    'error.loadingDashboard': 'Error Loading Dashboard',
    
    // Common
    'common.loading': 'Loading...',
    'common.at': 'at',
    'common.for': 'for',
    'common.male': 'Male',
    'common.female': 'Female',
    'common.years': 'years',
    'common.year': 'year',
    'common.update': 'Update',
    'common.create': 'Create',
    'common.required': 'is required',
    
    // Children Management
    'children.noChildrenYet': 'No children profiles added yet',
    'children.weeklyHours': 'Weekly Scheduled Hours',
    'children.noScheduledHours': 'No scheduled activity hours',
    'children.addFirstChild': 'Add Your First Child',
    'children.childAdded': 'Child added successfully',
    'children.childUpdated': 'Child updated successfully',
    'children.childDeleted': 'Child deleted successfully',
    'children.errorUpdating': 'Error updating child',
    'children.editChild': 'Edit Child',
    'children.deleteChild': 'Delete Child',
    'children.addChild': 'Add Child',
    'children.name': 'Name',
    'children.age': 'Age',
    'children.gender': 'Gender',
    'children.male': 'Male',
    'children.female': 'Female',
    'children.other': 'Other',
    'children.school': 'School',
    'children.grade': 'Grade',
    'children.interests': 'Interests',
    'children.colorCode': 'Color Code',
    'children.notes': 'Notes',
    'children.cancel': 'Cancel',
    'children.confirmDelete': 'Confirm Deletion',
    'children.confirmDeleteMessage': 'Are you sure you want to delete this child profile?',
    'children.delete': 'Delete',
    
    // Calendar
    'calendar.loading': 'Loading calendar...',
    'calendar.errorLoading': 'Failed to load calendar data',
    'calendar.errorUpdating': 'Failed to update activity',
    'calendar.noActivitiesScheduled': 'No activities scheduled',
    'calendar.noActivitiesThisWeek': 'No activities this week',
    'calendar.weekOf': 'Week of',
    
    // Activities
    'activities.title': 'Activities',
    'activities.loading': 'Loading activities...',
    'activities.noActivities': 'No activities added yet. Click "Add Activity" to get started.',
    'activities.allChildren': 'All Children',
    'activities.addActivity': 'Add Activity',
    'activities.editActivity': 'Edit Activity',
    'activities.deleteActivity': 'Delete Activity',
    'activities.activityAdded': 'Activity added successfully',
    'activities.activityUpdated': 'Activity updated successfully',
    'activities.activityDeleted': 'Activity deleted successfully',
    'activities.errorAdding': 'Error adding activity',
    'activities.errorUpdating': 'Error updating activity',
    'activities.errorDeleting': 'Error deleting activity',
    
    // Landing Page
    'landing.header.login': 'Login',
    'landing.hero.title': 'Transform Family Chaos into Harmony',
    'landing.hero.subtitle': 'The smart family activity manager that brings peace to your busy life',
    'landing.hero.cta': 'Start Free Today',
    'landing.hero.tagline': 'Join 1000+ families organizing better',
    
    // Features
    'landing.features.title': 'Everything Your Family Needs',
    'landing.features.subtitle': 'Simple tools that make a real difference',
    'landing.features.calendar.title': 'Smart Calendar',
    'landing.features.calendar.desc': 'See everyone\'s schedule in one place',
    'landing.features.reminders.title': 'Never Miss Again',
    'landing.features.reminders.desc': 'Automatic reminders for activities and pickups',
    'landing.features.sharing.title': 'Co-Parent Friendly',
    'landing.features.sharing.desc': 'Share schedules seamlessly with partners',
    'landing.features.children.title': 'Multiple Kids',
    'landing.features.children.desc': 'Manage all your children in one app',
    
    // New Features (Phase 2)
    'landing.newFeatures.title': 'New Features',
    'landing.newFeatures.subtitle': 'Powerful tools to organize your family life',
    'landing.newFeatures.badge': 'NEW',
    'landing.newFeatures.chores.title': 'Chore Chart',
    'landing.newFeatures.chores.desc': 'Track and reward household chores. Assign tasks to kids with points and schedules.',
    'landing.newFeatures.tasks.title': 'To-Do Lists',
    'landing.newFeatures.tasks.desc': 'Manage family tasks with priorities and due dates. Never forget important things again.',
    'landing.newFeatures.grocery.title': 'Grocery Lists',
    'landing.newFeatures.grocery.desc': 'Organize shopping with categorized lists. Share with family members and check off items as you shop.',
    
    // Testimonials
    'landing.testimonials.title': 'What Parents Are Saying',
    'landing.testimonials.sarah.name': 'Sarah Mitchell',
    'landing.testimonials.sarah.role': 'Working Mom of 2',
    'landing.testimonials.sarah.quote': 'Kivity saved my sanity. No more sticky notes or forgotten activities!',
    'landing.testimonials.david.name': 'David Williams',
    'landing.testimonials.david.role': 'Co-Parenting Dad',
    'landing.testimonials.david.quote': 'Finally, coordination with my ex-wife is drama-free.',
    'landing.testimonials.emma.name': 'Emma Johnson',
    'landing.testimonials.emma.role': 'Busy Parent',
    'landing.testimonials.emma.quote': 'This app understands the real challenges of parenting.',
    
    // CTA Section
    'landing.cta.title': 'Ready to Simplify Your Life?',
    'landing.cta.subtitle': 'Join thousands of families who have found peace in the chaos',
    'landing.cta.button': 'Get Started Free',
    
    // Footer
    'landing.footer.tagline': 'Built with ❤️ for families everywhere',
    'landing.footer.version': 'v 1.10.9',
    'landing.footer.links': 'Quick Links',
    'landing.footer.about': 'About',
    'landing.footer.contact': 'Contact',
    'landing.footer.privacy': 'Privacy',

    // Co-Parent Management
    'coparent.unlockTitle': 'Unlock Co-Parent Management',
    'coparent.unlockSubtitle': 'Seamlessly coordinate with ex-partners, grandparents, or other guardians by sharing access to your children\'s schedules and activities.',
    'coparent.shareSchedules': 'Share Schedules Easily',
    'coparent.shareSchedulesDesc': 'Invite other caregivers to view and contribute to your children\'s calendars.',
    'coparent.coordinateHouseholds': 'Coordinate Between Households',
    'coparent.coordinateHouseholdsDesc': 'Manage activities, appointments, and important events across different homes.',
    'coparent.stayUpdated': 'Stay Updated',
    'coparent.stayUpdatedDesc': 'Keep everyone informed about schedule changes and new activities in real-time.',
    'coparent.ensureConsistency': 'Ensure Consistency',
    'coparent.ensureConsistencyDesc': 'Promote stability for your child by maintaining a consistent routine across homes.',
    'coparent.upgradeToPremium': 'Upgrade to Premium',
    'coparent.lifetimeAccess': 'A one-time payment unlocks lifetime access to Co-Parent Management and all other premium features!',
    'coparent.title': 'Co-Parent Management',
    'coparent.subtitle': 'Coordinate your children\'s schedules with other caregivers',
    'coparent.aboutTitle': 'About Co-Parenting',
    'coparent.aboutDesc': 'Co-parenting allows you to share your children\'s activities and schedules with other caregivers. Invite another parent, grandparent, or guardian to establish a co-parenting relationship. Once connected, you\'ll both see shared children\'s activities and can coordinate schedules.',
    'coparent.invitationsTab': 'Invitations',
    'coparent.currentTab': 'Current Co-Parents',
    'coparent.activeTab': 'Active Co-Parents',
    'coparent.pendingTab': 'Pending Invitations',
    'coparent.tipsTitle': 'Tips for Successful Co-Parenting',
    'coparent.tip1': 'Always ensure the email address is correct before sending an invitation',
    'coparent.tip2': 'If the other parent doesn\'t see your invitation, ask them to check their account with the email you used',
    'coparent.tip3': 'You can remove a co-parent relationship at any time',
    'coparent.tip4': 'Each child can be shared with multiple co-parents',

    // Co-Parent Invitations Component
    'coparent.invitation.premiumFeature': 'Premium Feature: Co-Parent Invitations',
    'coparent.invitation.premiumDesc': 'Invite co-parents to collaborate on children\'s schedules and activities for just $4.99 (one-time fee).',
    'coparent.invitation.upgradeButton': 'Upgrade',
    'coparent.invitation.sentTab': 'Sent Invitations',
    'coparent.invitation.receivedTab': 'Received Invitations',
    'coparent.invitation.refreshButton': 'Refresh Invitations',
    'coparent.invitation.sendButton': 'Send Invitation',
    'coparent.invitation.premiumButton': 'Premium Feature',
    'coparent.invitation.upgradeTitle': 'Upgrade to Premium',
    'coparent.invitation.premiumFeatureTitle': 'Co-Parent Invitations - Premium Feature',
    'coparent.invitation.premiumPaymentDesc': 'The ability to invite co-parents is a premium feature available for a one-time payment of $4.99.',
    'coparent.invitation.benefitsTitle': 'Benefits of Premium:',
    'coparent.invitation.benefit1': 'Invite unlimited co-parents to collaborate on your children\'s schedules',
    'coparent.invitation.benefit2': 'Share events, appointments and activities seamlessly',
    'coparent.invitation.benefit3': 'Coordinate childcare and responsibilities with ease',
    'coparent.invitation.benefit4': 'One-time payment - never expires',
    'coparent.invitation.noteExisting': 'Note: All existing co-parent relationships will continue working even without premium.',
    'coparent.invitation.upgradeFor499': 'Upgrade for $4.99',
    'coparent.invitation.cancelButton': 'Cancel',
    'coparent.invitation.inviteTitle': 'Invite Co-Parent',
    'coparent.invitation.inviteInfo': 'When you invite someone to be a co-parent:',
    'coparent.invitation.inviteInfo1': 'If they already have a Kivity account, they\'ll see the invitation here',
    'coparent.invitation.inviteInfo2': 'If they don\'t have an account yet, the system will invite them to create one',
    'coparent.invitation.emailLabel': 'Co-Parent Email',
    'coparent.invitation.emailPlaceholder': 'Enter the email address of the other parent',
    'coparent.invitation.emailHelper': 'This should be the email they use (or will use) for their Kivity account',
    'coparent.invitation.childLabel': 'Child to Share',
    'coparent.invitation.noSent': 'No sent invitations yet.',
    'coparent.invitation.sendFirst': 'Send your first invitation',
    'coparent.invitation.to': 'To',
    'coparent.invitation.child': 'Child',
    'coparent.invitation.status': 'Status',
    'coparent.invitation.sent': 'Sent',
    'coparent.invitation.removeButton': 'Remove Co-parent',
    'coparent.invitation.confirmRemove': 'Are you sure you want to remove this co-parent relationship? This action cannot be undone.',
    'coparent.invitation.noReceived': 'No received invitations.',
    'coparent.invitation.from': 'From',
    'coparent.invitation.received': 'Received',
    'coparent.invitation.statusPending': 'Pending',
    'coparent.invitation.statusAccepted': 'Accepted',
    'coparent.invitation.statusRejected': 'Rejected',
    'coparent.invitation.statusExpired': 'Expired',
    'coparent.invitation.errorPremiumRequired': 'You need to be a premium user to send co-parent invitations.',
    'coparent.invitation.errorChildNotFound': 'Selected child not found',
    'coparent.invitation.successSent': 'Invitation sent successfully!',
    'coparent.invitation.successCancelled': 'Invitation cancelled successfully',
    'coparent.invitation.successAccepted': 'Invitation accepted successfully',
    'coparent.invitation.successDeclined': 'Invitation declined successfully',
    'coparent.invitation.errorCoParentNotFound': 'Co-parent user not found',
    'coparent.invitation.errorProfileNotFound': 'Could not find user profiles',
    'coparent.invitation.successRemoved': 'Co-parent relationship removed successfully',
    'coparent.invitation.errorRemoveFailed': 'Failed to remove co-parent',

    // Co-Parent List Component
    'coparent.list.title': 'Current Co-Parents',
    'coparent.list.refreshButton': 'Refresh list',
    'coparent.list.noSharedChildren': 'No shared children',
    'coparent.list.noCoParents': 'No co-parents found',
    'coparent.list.editTitle': 'Edit Shared Children',
    'coparent.list.editDesc': 'Select which children to share with',
    'coparent.list.noChildren': 'No children available to share',
    'coparent.list.cancelButton': 'Cancel',
    'coparent.list.saveButton': 'Save',
    'coparent.list.removeTitle': 'Remove Co-Parent',
    'coparent.list.removeConfirm': 'Are you sure you want to remove',
    'coparent.list.removeButton': 'Remove',
    'coparent.list.successRemoved': 'Successfully removed',
    'coparent.list.successUpdated': 'Successfully updated shared children',
    'coparent.list.errorLoading': 'Failed to load co-parent list',
    'coparent.list.errorRemoving': 'Failed to remove co-parent',
    'coparent.list.errorUpdating': 'Failed to update shared children',

    // Calendar Page
    'calendar.title': 'Calendar',
    'calendar.daily': 'Daily',
    'calendar.weekly': 'Weekly',
    'calendar.monthly': 'Monthly',
    'calendar.exportPDF': 'Export PDF',
    'calendar.activitiesFor': 'Activities for',
    'calendar.today': 'Today',
    'calendar.weeklySchedule': 'Weekly Schedule',
    'calendar.prev': 'Prev',
    'calendar.thisWeek': 'This Week',
    'calendar.next': 'Next',
    'calendar.time': 'Time',
    'calendar.allDay': 'All Day',
    'calendar.am': 'AM (12AM-12PM)',
    'calendar.pm': 'PM (12PM-12AM)',
    'calendar.hideEmptySlots': 'Hide empty slots',
    'calendar.noActivitiesScheduledFor': 'No activities scheduled for',
    'calendar.duringThisPeriod': 'during this time period',
    'calendar.showAllTimeSlots': 'Show all time slots',
    'calendar.noActivitiesDuringPeriod': 'No activities scheduled during this time period.',
    'calendar.pdfExportTitle': 'Calendar PDF Export',
    'calendar.pdfExportDesc': 'Export your calendar to PDF to print or share.',
    'calendar.downloadPDF': 'Download PDF',
    'calendar.close': 'Close',
    'calendar.generatingPDF': 'Generating PDF...',
    'calendar.pdfDownloadSuccess': 'PDF downloaded successfully!',
    'calendar.pdfError': 'Error generating PDF',
    'calendar.monday': 'Monday',
    'calendar.tuesday': 'Tuesday',
    'calendar.wednesday': 'Wednesday',
    'calendar.thursday': 'Thursday',
    'calendar.friday': 'Friday',
    'calendar.saturday': 'Saturday',
    'calendar.sunday': 'Sunday',
    'calendar.mon': 'Mon',
    'calendar.tue': 'Tue',
    'calendar.wed': 'Wed',
    'calendar.thu': 'Thu',
    'calendar.fri': 'Fri',
    'calendar.sat': 'Sat',
    'calendar.sun': 'Sun',
    'calendar.moreActivities': 'more activities',
    'calendar.editActivity': 'Edit Activity',
    'calendar.addActivity': 'Add Activity',

    // Profile Page
    'profile.title': 'User Profile',
    'profile.subtitle': 'Manage your account information and settings',
    'profile.removePhoto': 'Remove Photo',
    'profile.accountInfo': 'Account Information',
    'profile.edit': 'Edit',
    'profile.save': 'Save',
    'profile.firstName': 'First Name',
    'profile.lastName': 'Last Name',
    'profile.name': 'Name',
    'profile.notSet': 'Not set',
    'profile.email': 'Email',
    'profile.notAvailable': 'Not available',
    'profile.accountCreated': 'Account created',
    'profile.changePassword': 'Change Password',
    'profile.settings': 'Settings',
    'profile.notifications': 'Notifications',
    'profile.notificationSettings': 'Notification Settings',
    'profile.notificationDesc': 'Configure how you want to receive notifications about your children\'s activities.',
    'profile.configureNotifications': 'Configure Notifications',
    'profile.subscription': 'Subscription',
    'profile.premiumPlan': 'You are currently on the <strong>Premium Plan</strong>!',
    'profile.premiumDesc': 'Enjoy all premium features, including co-parent coordination and unlimited child profiles.',
    'profile.freePlan': 'You are currently on the <strong>Free Plan</strong>.',
    'profile.upgradeToPremium': 'Upgrade to Premium',
    'profile.changePasswordTitle': 'Change Password',
    'profile.changePasswordDesc': 'To change your password, please enter your current password and choose a new one.',
    'profile.currentPassword': 'Current Password',
    'profile.newPassword': 'New Password',
    'profile.confirmPassword': 'Confirm New Password',
    'profile.passwordMinLength': 'Password must be at least 6 characters',
    'profile.cancel': 'Cancel',
    'profile.updatePassword': 'Update Password',
    'profile.passwordUpdated': 'Password updated successfully!',
    'profile.profileUpdated': 'Profile updated successfully!',
    'profile.errorUploadImage': 'Please select an image file',
    'profile.errorImageSize': 'Image must be smaller than 5MB',
    'profile.errorReadingImage': 'Error reading image file',
    'profile.errorUploadingImage': 'Error uploading image. Please try again.',
    'profile.errorRemovingPhoto': 'Failed to remove profile picture',
    'profile.errorFirstNameRequired': 'First name is required',
    'profile.errorUpdateProfile': 'Failed to update profile',
    'profile.errorCurrentPassword': 'Current password is required',
    'profile.errorNewPassword': 'New password is required',
    'profile.errorPasswordLength': 'New password must be at least 6 characters',
    'profile.errorPasswordMismatch': 'Passwords do not match',
    'profile.errorUpdatePassword': 'Failed to update password',

    // Settings Page
    'settings.title': 'Settings',
    'settings.notifications': 'Notifications',
    'settings.emailNotifications': 'Email Notifications',
    'settings.emailNotificationsDesc': 'Receive email notifications about upcoming activities and schedule changes.',
    'settings.configureSmartReminders': 'Configure Smart Reminders',
    'settings.configureSmartRemindersDesc': 'Set up advanced notification preferences, reminder timing, and conflict alerts.',
    'settings.appearance': 'Appearance',
    'settings.darkMode': 'Dark Mode',
    'settings.darkModeDesc': 'Switch between light and dark theme.',
    'settings.light': 'Light',
    'settings.dark': 'Dark',
    'settings.calendarIntegration': 'Calendar Integration',
    'settings.syncExternalCalendars': 'Sync with External Calendars',
    'settings.syncExternalCalendarsDesc': 'Synchronize activities with Google Calendar, Apple Calendar, or Outlook.',
    'settings.premiumFeatures': 'Premium Features',
    'settings.active': 'Active',
    'settings.yourPremiumAccount': 'Your Premium Account',
    'settings.upgradeToPremium': 'Upgrade to Premium',
    'settings.premiumThankYou': 'Thank you for supporting Kivity! You have access to all premium features.',
    'settings.premiumUnlockDesc': 'Unlock advanced features to make managing your children\'s activities even easier.',
    'settings.premiumFeaturesInclude': 'Premium Features Include:',
    'settings.coParentCoordination': 'Co-Parent Coordination',
    'settings.coParentCoordinationDesc': 'Share schedules with other caregivers',
    'settings.calendarSync': 'Calendar Sync',
    'settings.calendarSyncDesc': 'Sync with external calendars',
    'settings.manageCoParents': 'Manage Co-Parents',
    'settings.billingPayments': 'Billing & Payments',
    'settings.billingPaymentsDesc': 'View your payment history, download invoices, and manage billing information.',
    'settings.paymentHistory': 'Payment History',
    'settings.downloadAllInvoices': 'Download All Invoices',
    'settings.downloadInvoicesComingSoon': 'Download invoices feature coming soon!',
    'settings.currentPlan': 'Current Plan',
    'settings.premiumOneTime': 'Premium (One-time payment)',
    'settings.upgraded': 'Upgraded',
    'settings.lightModeEnabled': 'Light mode enabled',
    'settings.darkModeEnabled': 'Dark mode enabled',
    'settings.emailNotificationsEnabled': 'Email notifications enabled',
    'settings.emailNotificationsDisabled': 'Email notifications disabled',
    'settings.calendarSyncEnabled': 'Calendar sync enabled',
    'settings.calendarSyncDisabled': 'Calendar sync disabled',

    // Chores
    'chores.title': 'Chore Chart',
    'chores.addChore': 'Add Chore',
    'chores.editChore': 'Edit Chore',
    'chores.deleteChore': 'Delete Chore',
    'chores.choreTitle': 'Chore Title',
    'chores.description': 'Description',
    'chores.frequency': 'Frequency',
    'chores.daily': 'Daily',
    'chores.weekly': 'Weekly',
    'chores.monthly': 'Monthly',
    'chores.oneTime': 'One-Time',
    'chores.daysOfWeek': 'Days of Week',
    'chores.assignTo': 'Assign To',
    'chores.familyAll': 'Family (All)',
    'chores.points': 'Points (Optional)',
    'chores.all': 'All',
    'chores.pending': 'Pending',
    'chores.completed': 'Completed',
    'chores.noChores': 'No chores yet',
    'chores.noPendingChores': 'No pending chores',
    'chores.noCompletedChores': 'No completed chores',
    'chores.addFirstChore': 'Add Your First Chore',
    'chores.choreCreated': 'Chore created successfully',
    'chores.choreUpdated': 'Chore updated successfully',
    'chores.choreDeleted': 'Chore deleted successfully',
    'chores.failedToLoad': 'Failed to load chores',
    'chores.failedToSave': 'Failed to save chore',
    'chores.failedToDelete': 'Failed to delete chore',
    'chores.confirmDelete': 'Delete Chore?',
    'chores.confirmDeleteMessage': 'Are you sure you want to delete this chore? This action cannot be undone.',

    // Tasks
    'tasks.title': 'To-Do Lists',
    'tasks.addTask': 'Add Task',
    'tasks.editTask': 'Edit Task',
    'tasks.deleteTask': 'Delete Task',
    'tasks.taskTitle': 'Task Title',
    'tasks.description': 'Description',
    'tasks.priority': 'Priority',
    'tasks.priorityLow': 'Low',
    'tasks.priorityMedium': 'Medium',
    'tasks.priorityHigh': 'High',
    'tasks.assignTo': 'Assign To',
    'tasks.familyAll': 'Family (All)',
    'tasks.meParent': 'Me (Parent)',
    'tasks.specificChild': 'Specific Child',
    'tasks.dueDate': 'Due Date',
    'tasks.category': 'Category (Optional)',
    'tasks.categoryPlaceholder': 'e.g., Shopping, Appointments, School',
    'tasks.all': 'All',
    'tasks.pending': 'Pending',
    'tasks.completed': 'Completed',
    'tasks.noTasks': 'No tasks yet',
    'tasks.noPendingTasks': 'No pending tasks',
    'tasks.noCompletedTasks': 'No completed tasks',
    'tasks.addFirstTask': 'Add Your First Task',
    'tasks.taskCreated': 'Task created successfully',
    'tasks.taskUpdated': 'Task updated successfully',
    'tasks.taskDeleted': 'Task deleted successfully',
    'tasks.failedToLoad': 'Failed to load tasks',
    'tasks.failedToSave': 'Failed to save task',
    'tasks.failedToDelete': 'Failed to delete task',
    'tasks.overdue': 'Overdue',
    'tasks.confirmDelete': 'Delete Task?',
    'tasks.confirmDeleteMessage': 'Are you sure you want to delete this task? This action cannot be undone.',

    // Grocery
    'grocery.title': 'Grocery Lists',
    'grocery.newList': 'New List',
    'grocery.editList': 'Edit List',
    'grocery.deleteList': 'Delete List',
    'grocery.listName': 'List Name',
    'grocery.description': 'Description (Optional)',
    'grocery.addItem': 'Add Item',
    'grocery.editItem': 'Edit Item',
    'grocery.itemName': 'Item Name',
    'grocery.quantity': 'Quantity (Optional)',
    'grocery.quantityPlaceholder': 'e.g., 2 lbs, 1 pack',
    'grocery.category': 'Category',
    'grocery.categoryProduce': 'Produce',
    'grocery.categoryDairy': 'Dairy',
    'grocery.categoryMeat': 'Meat',
    'grocery.categoryBakery': 'Bakery',
    'grocery.categoryFrozen': 'Frozen',
    'grocery.categoryPantry': 'Pantry',
    'grocery.categoryBeverages': 'Beverages',
    'grocery.categoryOther': 'Other',
    'grocery.notes': 'Notes (Optional)',
    'grocery.noLists': 'No grocery lists yet',
    'grocery.noItems': 'No items yet',
    'grocery.createFirstList': 'Create Your First List',
    'grocery.listCreated': 'List created successfully',
    'grocery.listUpdated': 'List updated successfully',
    'grocery.listDeleted': 'List deleted successfully',
    'grocery.itemAdded': 'Item added successfully',
    'grocery.itemUpdated': 'Item updated successfully',
    'grocery.itemDeleted': 'Item deleted successfully',
    'grocery.failedToLoad': 'Failed to load grocery lists',
    'grocery.failedToSave': 'Failed to save list',
    'grocery.failedToDelete': 'Failed to delete list',
    'grocery.confirmDeleteList': 'Are you sure you want to delete this list? All items will be deleted.',
    'grocery.items': 'items',
  },
  zh: {
    // Navigation
    'nav.dashboard': '控制台',
    'nav.children': '孩子',
    'nav.activities': '活动',
    'nav.calendar': '日历',
    'nav.chores': '家务',
    'nav.tasks': '任务',
    'nav.grocery': '购物清单',
    'nav.coparents': '共同家长',
    'nav.settings': '设置',
    'nav.profile': '个人资料',
    'nav.about': '关于',
    'nav.logout': '退出登录',
    'nav.adminPanel': '管理面板',
    
    // Dashboard
    'dashboard.title': '控制台',
    'dashboard.welcome': '控制台',
    'dashboard.loading': '正在加载您的控制台...',
    'dashboard.todayOverview': '今日概览',
    'dashboard.completed': '已完成',
    'dashboard.upcoming': '即将到来',
    'dashboard.noActivities': '今日没有安排活动。',
    'dashboard.hasActivities': '您今天有 {count} 个活动。',
    'dashboard.addActivity': '添加活动',
    'dashboard.children': '孩子们',
    'dashboard.viewAll': '查看全部',
    'dashboard.viewCalendar': '查看日历',
    'dashboard.noChildren': '还没有添加孩子。',
    'dashboard.addChild': '添加孩子',
    'dashboard.todayActivities': '今日活动',
    'dashboard.upcomingActivities': '即将到来的活动',
    'dashboard.noActivitiesToday': '今天没有安排活动',
    'dashboard.noUpcomingActivities': '未来7天没有即将到来的活动',
    'dashboard.scheduleActivity': '安排活动',
    'dashboard.offline': '您已离线',
    'dashboard.online': '您已在线',
    'dashboard.schedulingConflicts': '时间安排冲突',
    
    // Activity Status
    'activity.completed': '已完成',
    'activity.inProgress': '进行中',
    'activity.comingUp': '即将开始',
    'activity.planned': '已计划',
    
    // Notifications
    'notification.offline': '您已离线',
    'notification.offlineMessage': '显示缓存数据。重新联网后将同步更改。',
    'notification.enableNotifications': '启用通知',
    'notification.notificationMessage': '获取即将到来的活动提醒和时间表警报。',
    'notification.enable': '启用',
    'notification.enabling': '启用中...',
    'notification.enabled': '通知启用成功！',
    'notification.denied': '通知权限被拒绝。您可以稍后在浏览器设置中启用。',
    'notification.failed': '启用通知失败。请重试或检查您的浏览器设置。',
    'notification.conflictDetected': '检测到时间安排冲突',
    'notification.upcomingActivity': '即将到来的活动',
    'notification.online': '在线',
    'notification.offlineStatus': '离线',
    'notification.retry': '点击重试连接',
    'notification.connected': '您已在线并连接',
    'notification.reconnecting': '正在尝试重新连接...',
    
    // Error Messages
    'error.something_wrong': '哎呀！出了点问题',
    'error.refresh': '刷新页面',
    'error.loadingDashboard': '加载控制台出错',
    
    // Common
    'common.loading': '正在加载...',
    'common.at': '在',
    'common.for': '为',
    'common.male': '男',
    'common.female': '女',
    'common.years': '岁',
    'common.year': '岁',
    'common.update': '更新',
    'common.create': '创建',
    'common.required': '是必填项',
    
    // Children Management
    'children.noChildrenYet': '还没有添加孩子资料',
    'children.weeklyHours': '每周安排小时',
    'children.noScheduledHours': '没有安排的活动时间',
    'children.addFirstChild': '添加您的第一个孩子',
    'children.childAdded': '孩子已添加',
    'children.childUpdated': '孩子信息已更新',
    'children.childDeleted': '孩子已删除',
    'children.errorUpdating': '更新孩子信息时出错',
    'children.editChild': '编辑孩子',
    'children.deleteChild': '删除孩子',
    'children.addChild': '添加孩子',
    'children.name': '姓名',
    'children.age': '年龄',
    'children.gender': '性别',
    'children.male': '男',
    'children.female': '女',
    'children.other': '其他',
    'children.school': '学校',
    'children.grade': '年级',
    'children.interests': '兴趣',
    'children.colorCode': '颜色代码',
    'children.notes': '备注',
    'children.cancel': '取消',
    'children.confirmDelete': '确认删除',
    'children.confirmDeleteMessage': '您确定要删除这个孩子的资料吗？',
    'children.delete': '删除',
    
    // Calendar
    'calendar.loading': '正在加载日历...',
    'calendar.errorLoading': '无法加载日历数据',
    'calendar.errorUpdating': '无法更新活动',
    'calendar.noActivitiesScheduled': '没有安排活动',
    'calendar.noActivitiesThisWeek': '本周没有活动',
    'calendar.weekOf': '周',
    
    // Activities
    'activities.title': '活动',
    'activities.loading': '正在加载活动...',
    'activities.noActivities': '还没有添加活动。点击"添加活动"开始吧。',
    'activities.allChildren': '所有孩子',
    'activities.addActivity': '添加活动',
    'activities.editActivity': '编辑活动',
    'activities.deleteActivity': '删除活动',
    'activities.activityAdded': '活动添加成功',
    'activities.activityUpdated': '活动更新成功',
    'activities.activityDeleted': '活动删除成功',
    'activities.errorAdding': '添加活动时出错',
    'activities.errorUpdating': '更新活动时出错',
    'activities.errorDeleting': '删除活动时出错',
    
    // Landing Page
    'landing.header.login': '登录',
    'landing.hero.title': '让家庭生活从混乱走向和谐',
    'landing.hero.subtitle': '智能家庭活动管理器，为您的忙碌生活带来宁静',
    'landing.hero.cta': '免费开始使用',
    'landing.hero.tagline': '已有1000+家庭选择我们',
    
    // Features
    'landing.features.title': '您家庭所需的一切功能',
    'landing.features.subtitle': '简单工具，真正改变',
    'landing.features.calendar.title': '智能日历',
    'landing.features.calendar.desc': '在一个地方查看全家的日程安排',
    'landing.features.reminders.title': '永不遗忘',
    'landing.features.reminders.desc': '活动和接送的自动提醒',
    'landing.features.sharing.title': '共育友好',
    'landing.features.sharing.desc': '与伴侣无缝分享日程安排',
    'landing.features.children.title': '多孩管理',
    'landing.features.children.desc': '在一个应用中管理所有孩子',
    
    // New Features (Phase 2)
    'landing.newFeatures.title': '新功能',
    'landing.newFeatures.subtitle': '强大的工具来组织您的家庭生活',
    'landing.newFeatures.badge': '新',
    'landing.newFeatures.chores.title': '家务表',
    'landing.newFeatures.chores.desc': '跟踪和奖励家务。给孩子分配任务，设置积分和日程安排。',
    'landing.newFeatures.tasks.title': '待办事项',
    'landing.newFeatures.tasks.desc': '管理家庭任务，设置优先级和截止日期。再也不会忘记重要的事情。',
    'landing.newFeatures.grocery.title': '购物清单',
    'landing.newFeatures.grocery.desc': '用分类清单组织购物。与家人分享，购物时勾选商品。',
    
    // Testimonials
    'landing.testimonials.title': '家长们的反馈',
    'landing.testimonials.sarah.name': '莎拉·米切尔',
    'landing.testimonials.sarah.role': '职场妈妈，2个孩子',
    'landing.testimonials.sarah.quote': 'Kivity拯救了我的理智。再也不用便签纸，不会忘记活动了！',
    'landing.testimonials.david.name': '大卫·威廉姆斯',
    'landing.testimonials.david.role': '共育爸爸',
    'landing.testimonials.david.quote': '终于，与前妻的协调不再有戏剧性了。',
    'landing.testimonials.emma.name': '艾玛·约翰逊',
    'landing.testimonials.emma.role': '忙碌家长',
    'landing.testimonials.emma.quote': '这个应用真正理解育儿的挑战。',
    
    // CTA Section
    'landing.cta.title': '准备简化您的生活吗？',
    'landing.cta.subtitle': '加入数千个在混乱中找到宁静的家庭',
    'landing.cta.button': '免费开始',
    
    // Footer
    'landing.footer.tagline': '用❤️为全世界的家庭打造',
    'landing.footer.version': '版本 1.10.9',
    'landing.footer.links': '快速链接',
    'landing.footer.about': '关于我们',
    'landing.footer.contact': '联系我们',
    'landing.footer.privacy': '隐私政策',

    // Co-Parent Management
    'coparent.unlockTitle': '解锁共同家长管理',
    'coparent.unlockSubtitle': '与前任伴侣、祖父母或其他监护人无缝协调，共享孩子的日程和活动。',
    'coparent.shareSchedules': '轻松分享日程',
    'coparent.shareSchedulesDesc': '邀请其他照顾者查看和贡献您孩子的日历。',
    'coparent.coordinateHouseholds': '协调不同家庭',
    'coparent.coordinateHouseholdsDesc': '管理不同家庭之间的活动、预约和重要事件。',
    'coparent.stayUpdated': '保持更新',
    'coparent.stayUpdatedDesc': '实时通知所有人日程变更和新活动。',
    'coparent.ensureConsistency': '确保一致性',
    'coparent.ensureConsistencyDesc': '通过在不同家庭保持一致的日常安排来促进孩子的稳定性。',
    'coparent.upgradeToPremium': '升级到高级版',
    'coparent.lifetimeAccess': '一次性付款即可终身访问共同家长管理和所有其他高级功能！',
    'coparent.title': '共同家长管理',
    'coparent.subtitle': '与其他照顾者协调您孩子的日程',
    'coparent.aboutTitle': '关于共同育儿',
    'coparent.aboutDesc': '共同育儿允许您与其他照顾者共享孩子的活动和日程。邀请另一位家长、祖父母或监护人建立共同育儿关系。建立连接后，您都可以查看共享孩子的活动并协调日程。',
    'coparent.invitationsTab': '邀请',
    'coparent.currentTab': '当前共同家长',
    'coparent.activeTab': '活跃共同家长',
    'coparent.pendingTab': '待处理邀请',
    'coparent.tipsTitle': '成功共同育儿的提示',
    'coparent.tip1': '发送邀请前始终确保电子邮件地址正确',
    'coparent.tip2': '如果另一位家长看不到您的邀请，请让他们使用您使用的电子邮件检查他们的帐户',
    'coparent.tip3': '您可以随时删除共同家长关系',
    'coparent.tip4': '每个孩子可以与多个共同家长共享',

    // Co-Parent Invitations Component
    'coparent.invitation.premiumFeature': '高级功能：共同家长邀请',
    'coparent.invitation.premiumDesc': '邀请共同家长协作管理孩子的日程和活动，仅需$4.99（一次性费用）。',
    'coparent.invitation.upgradeButton': '升级',
    'coparent.invitation.sentTab': '已发送邀请',
    'coparent.invitation.receivedTab': '已接收邀请',
    'coparent.invitation.refreshButton': '刷新邀请',
    'coparent.invitation.sendButton': '发送邀请',
    'coparent.invitation.premiumButton': '高级功能',
    'coparent.invitation.upgradeTitle': '升级到高级版',
    'coparent.invitation.premiumFeatureTitle': '共同家长邀请 - 高级功能',
    'coparent.invitation.premiumPaymentDesc': '邀请共同家长的功能是一项高级功能，一次性支付$4.99即可使用。',
    'coparent.invitation.benefitsTitle': '高级版的优势：',
    'coparent.invitation.benefit1': '邀请无限数量的共同家长协作管理孩子的日程',
    'coparent.invitation.benefit2': '无缝分享活动、预约和安排',
    'coparent.invitation.benefit3': '轻松协调照顾和责任',
    'coparent.invitation.benefit4': '一次性付款 - 永久有效',
    'coparent.invitation.noteExisting': '注意：即使没有高级版，所有现有的共同家长关系仍将继续工作。',
    'coparent.invitation.upgradeFor499': '升级 - $4.99',
    'coparent.invitation.cancelButton': '取消',
    'coparent.invitation.inviteTitle': '邀请共同家长',
    'coparent.invitation.inviteInfo': '当您邀请某人成为共同家长时：',
    'coparent.invitation.inviteInfo1': '如果他们已经有Kivity账户，他们将在这里看到邀请',
    'coparent.invitation.inviteInfo2': '如果他们还没有账户，系统将邀请他们创建一个',
    'coparent.invitation.emailLabel': '共同家长电子邮件',
    'coparent.invitation.emailPlaceholder': '输入另一位家长的电子邮件地址',
    'coparent.invitation.emailHelper': '这应该是他们使用（或将使用）Kivity账户的电子邮件',
    'coparent.invitation.childLabel': '要分享的孩子',
    'coparent.invitation.noSent': '还没有发送邀请。',
    'coparent.invitation.sendFirst': '发送您的第一个邀请',
    'coparent.invitation.to': '发送至',
    'coparent.invitation.child': '孩子',
    'coparent.invitation.status': '状态',
    'coparent.invitation.sent': '发送时间',
    'coparent.invitation.removeButton': '移除共同家长',
    'coparent.invitation.confirmRemove': '您确定要移除此共同家长关系吗？此操作无法撤消。',
    'coparent.invitation.noReceived': '没有收到邀请。',
    'coparent.invitation.from': '来自',
    'coparent.invitation.received': '接收时间',
    'coparent.invitation.statusPending': '待处理',
    'coparent.invitation.statusAccepted': '已接受',
    'coparent.invitation.statusRejected': '已拒绝',
    'coparent.invitation.statusExpired': '已过期',
    'coparent.invitation.errorPremiumRequired': '您需要成为高级用户才能发送共同家长邀请。',
    'coparent.invitation.errorChildNotFound': '未找到所选孩子',
    'coparent.invitation.successSent': '邀请发送成功！',
    'coparent.invitation.successCancelled': '邀请已成功取消',
    'coparent.invitation.successAccepted': '邀请已成功接受',
    'coparent.invitation.successDeclined': '邀请已成功拒绝',
    'coparent.invitation.errorCoParentNotFound': '未找到共同家长用户',
    'coparent.invitation.errorProfileNotFound': '找不到用户资料',
    'coparent.invitation.successRemoved': '共同家长关系已成功移除',
    'coparent.invitation.errorRemoveFailed': '移除共同家长失败',

    // Co-Parent List Component
    'coparent.list.title': '当前共同家长',
    'coparent.list.refreshButton': '刷新列表',
    'coparent.list.noSharedChildren': '没有共享的孩子',
    'coparent.list.noCoParents': '未找到共同家长',
    'coparent.list.editTitle': '编辑共享的孩子',
    'coparent.list.editDesc': '选择要与以下用户共享的孩子',
    'coparent.list.noChildren': '没有可以分享的孩子',
    'coparent.list.cancelButton': '取消',
    'coparent.list.saveButton': '保存',
    'coparent.list.removeTitle': '移除共同家长',
    'coparent.list.removeConfirm': '您确定要移除',
    'coparent.list.removeButton': '移除',
    'coparent.list.successRemoved': '成功移除',
    'coparent.list.successUpdated': '成功更新共享的孩子',
    'coparent.list.errorLoading': '无法加载共同家长列表',
    'coparent.list.errorRemoving': '无法移除共同家长',
    'coparent.list.errorUpdating': '无法更新共享的孩子',

    // Calendar Page
    'calendar.title': '日历',
    'calendar.daily': '每日',
    'calendar.weekly': '每周',
    'calendar.monthly': '每月',
    'calendar.exportPDF': '导出PDF',
    'calendar.activitiesFor': '活动日期',
    'calendar.today': '今天',
    'calendar.weeklySchedule': '每周日程',
    'calendar.prev': '上一页',
    'calendar.thisWeek': '本周',
    'calendar.next': '下一页',
    'calendar.time': '时间',
    'calendar.allDay': '全天',
    'calendar.am': '上午 (12AM-12PM)',
    'calendar.pm': '下午 (12PM-12AM)',
    'calendar.hideEmptySlots': '隐藏空时段',
    'calendar.noActivitiesScheduledFor': '没有安排活动',
    'calendar.duringThisPeriod': '在此时间段',
    'calendar.showAllTimeSlots': '显示所有时间段',
    'calendar.noActivitiesDuringPeriod': '此时间段没有安排活动。',
    'calendar.pdfExportTitle': '日历PDF导出',
    'calendar.pdfExportDesc': '将您的日历导出为PDF以打印或分享。',
    'calendar.downloadPDF': '下载PDF',
    'calendar.close': '关闭',
    'calendar.generatingPDF': '正在生成PDF...',
    'calendar.pdfDownloadSuccess': 'PDF下载成功！',
    'calendar.pdfError': '生成PDF时出错',
    'calendar.monday': '星期一',
    'calendar.tuesday': '星期二',
    'calendar.wednesday': '星期三',
    'calendar.thursday': '星期四',
    'calendar.friday': '星期五',
    'calendar.saturday': '星期六',
    'calendar.sunday': '星期日',
    'calendar.mon': '周一',
    'calendar.tue': '周二',
    'calendar.wed': '周三',
    'calendar.thu': '周四',
    'calendar.fri': '周五',
    'calendar.sat': '周六',
    'calendar.sun': '周日',
    'calendar.moreActivities': '更多活动',
    'calendar.editActivity': '编辑活动',
    'calendar.addActivity': '添加活动',

    // Profile Page
    'profile.title': '用户资料',
    'profile.subtitle': '管理您的帐户信息和设置',
    'profile.removePhoto': '移除照片',
    'profile.accountInfo': '帐户信息',
    'profile.edit': '编辑',
    'profile.save': '保存',
    'profile.firstName': '名字',
    'profile.lastName': '姓氏',
    'profile.name': '姓名',
    'profile.notSet': '未设置',
    'profile.email': '电子邮件',
    'profile.notAvailable': '不可用',
    'profile.accountCreated': '帐户创建时间',
    'profile.changePassword': '更改密码',
    'profile.settings': '设置',
    'profile.notifications': '通知',
    'profile.notificationSettings': '通知设置',
    'profile.notificationDesc': '配置您希望如何接收有关孩子活动的通知。',
    'profile.configureNotifications': '配置通知',
    'profile.subscription': '订阅',
    'profile.premiumPlan': '您当前使用的是<strong>高级计划</strong>！',
    'profile.premiumDesc': '享受所有高级功能，包括共同家长协调和无限制的孩子资料。',
    'profile.freePlan': '您当前使用的是<strong>免费计划</strong>。',
    'profile.upgradeToPremium': '升级到高级版',
    'profile.changePasswordTitle': '更改密码',
    'profile.changePasswordDesc': '要更改密码，请输入您的当前密码并选择新密码。',
    'profile.currentPassword': '当前密码',
    'profile.newPassword': '新密码',
    'profile.confirmPassword': '确认新密码',
    'profile.passwordMinLength': '密码必须至少6个字符',
    'profile.cancel': '取消',
    'profile.updatePassword': '更新密码',
    'profile.passwordUpdated': '密码更新成功！',
    'profile.profileUpdated': '资料更新成功！',
    'profile.errorUploadImage': '请选择图像文件',
    'profile.errorImageSize': '图像必须小于5MB',
    'profile.errorReadingImage': '读取图像文件时出错',
    'profile.errorUploadingImage': '上传图像时出错。请重试。',
    'profile.errorRemovingPhoto': '删除个人资料图片失败',
    'profile.errorFirstNameRequired': '名字是必填项',
    'profile.errorUpdateProfile': '更新资料失败',
    'profile.errorCurrentPassword': '当前密码是必填项',
    'profile.errorNewPassword': '新密码是必填项',
    'profile.errorPasswordLength': '新密码必须至少6个字符',
    'profile.errorPasswordMismatch': '密码不匹配',
    'profile.errorUpdatePassword': '更新密码失败',

    // Settings Page
    'settings.title': '设置',
    'settings.notifications': '通知',
    'settings.emailNotifications': '电子邮件通知',
    'settings.emailNotificationsDesc': '接收有关即将到来的活动和日程变更的电子邮件通知。',
    'settings.configureSmartReminders': '配置智能提醒',
    'settings.configureSmartRemindersDesc': '设置高级通知首选项、提醒时间和冲突警报。',
    'settings.appearance': '外观',
    'settings.darkMode': '深色模式',
    'settings.darkModeDesc': '在浅色和深色主题之间切换。',
    'settings.light': '浅色',
    'settings.dark': '深色',
    'settings.calendarIntegration': '日历集成',
    'settings.syncExternalCalendars': '与外部日历同步',
    'settings.syncExternalCalendarsDesc': '与Google日历、Apple日历或Outlook同步活动。',
    'settings.premiumFeatures': '高级功能',
    'settings.active': '活跃',
    'settings.yourPremiumAccount': '您的高级账户',
    'settings.upgradeToPremium': '升级到高级版',
    'settings.premiumThankYou': '感谢您支持Kivity！您可以访问所有高级功能。',
    'settings.premiumUnlockDesc': '解锁高级功能，让管理孩子的活动更加轻松。',
    'settings.premiumFeaturesInclude': '高级功能包括：',
    'settings.coParentCoordination': '共同家长协调',
    'settings.coParentCoordinationDesc': '与其他照顾者分享日程',
    'settings.calendarSync': '日历同步',
    'settings.calendarSyncDesc': '与外部日历同步',
    'settings.manageCoParents': '管理共同家长',
    'settings.billingPayments': '账单与付款',
    'settings.billingPaymentsDesc': '查看您的付款历史、下载发票并管理账单信息。',
    'settings.paymentHistory': '付款历史',
    'settings.downloadAllInvoices': '下载所有发票',
    'settings.downloadInvoicesComingSoon': '下载发票功能即将推出！',
    'settings.currentPlan': '当前计划',
    'settings.premiumOneTime': '高级版（一次性付款）',
    'settings.upgraded': '升级时间',
    'settings.lightModeEnabled': '浅色模式已启用',
    'settings.darkModeEnabled': '深色模式已启用',
    'settings.emailNotificationsEnabled': '电子邮件通知已启用',
    'settings.emailNotificationsDisabled': '电子邮件通知已禁用',
    'settings.calendarSyncEnabled': '日历同步已启用',
    'settings.calendarSyncDisabled': '日历同步已禁用',

    // Chores (家务)
    'chores.title': '家务表',
    'chores.addChore': '添加家务',
    'chores.editChore': '编辑家务',
    'chores.deleteChore': '删除家务',
    'chores.choreTitle': '家务名称',
    'chores.description': '描述',
    'chores.frequency': '频率',
    'chores.daily': '每天',
    'chores.weekly': '每周',
    'chores.monthly': '每月',
    'chores.oneTime': '一次性',
    'chores.daysOfWeek': '星期几',
    'chores.assignTo': '分配给',
    'chores.familyAll': '全家',
    'chores.points': '积分（可选）',
    'chores.all': '全部',
    'chores.pending': '待完成',
    'chores.completed': '已完成',
    'chores.noChores': '还没有家务',
    'chores.noPendingChores': '没有待完成的家务',
    'chores.noCompletedChores': '没有已完成的家务',
    'chores.addFirstChore': '添加您的第一个家务',
    'chores.choreCreated': '家务创建成功',
    'chores.choreUpdated': '家务更新成功',
    'chores.choreDeleted': '家务删除成功',
    'chores.failedToLoad': '加载家务失败',
    'chores.failedToSave': '保存家务失败',
    'chores.failedToDelete': '删除家务失败',
    'chores.confirmDelete': '删除家务？',
    'chores.confirmDeleteMessage': '您确定要删除这个家务吗？此操作无法撤销。',

    // Tasks (任务)
    'tasks.title': '待办事项',
    'tasks.addTask': '添加任务',
    'tasks.editTask': '编辑任务',
    'tasks.deleteTask': '删除任务',
    'tasks.taskTitle': '任务名称',
    'tasks.description': '描述',
    'tasks.priority': '优先级',
    'tasks.priorityLow': '低',
    'tasks.priorityMedium': '中',
    'tasks.priorityHigh': '高',
    'tasks.assignTo': '分配给',
    'tasks.familyAll': '全家',
    'tasks.meParent': '我（家长）',
    'tasks.specificChild': '指定孩子',
    'tasks.dueDate': '截止日期',
    'tasks.category': '类别（可选）',
    'tasks.categoryPlaceholder': '例如：购物、预约、学校',
    'tasks.all': '全部',
    'tasks.pending': '待完成',
    'tasks.completed': '已完成',
    'tasks.noTasks': '还没有任务',
    'tasks.noPendingTasks': '没有待完成的任务',
    'tasks.noCompletedTasks': '没有已完成的任务',
    'tasks.addFirstTask': '添加您的第一个任务',
    'tasks.taskCreated': '任务创建成功',
    'tasks.taskUpdated': '任务更新成功',
    'tasks.taskDeleted': '任务删除成功',
    'tasks.failedToLoad': '加载任务失败',
    'tasks.failedToSave': '保存任务失败',
    'tasks.failedToDelete': '删除任务失败',
    'tasks.overdue': '已逾期',
    'tasks.confirmDelete': '删除任务？',
    'tasks.confirmDeleteMessage': '您确定要删除这个任务吗？此操作无法撤销。',

    // Grocery (购物清单)
    'grocery.title': '购物清单',
    'grocery.newList': '新建清单',
    'grocery.editList': '编辑清单',
    'grocery.deleteList': '删除清单',
    'grocery.listName': '清单名称',
    'grocery.description': '描述（可选）',
    'grocery.addItem': '添加商品',
    'grocery.editItem': '编辑商品',
    'grocery.itemName': '商品名称',
    'grocery.quantity': '数量（可选）',
    'grocery.quantityPlaceholder': '例如：2磅，1包',
    'grocery.category': '类别',
    'grocery.categoryProduce': '蔬果',
    'grocery.categoryDairy': '乳制品',
    'grocery.categoryMeat': '肉类',
    'grocery.categoryBakery': '烘焙',
    'grocery.categoryFrozen': '冷冻',
    'grocery.categoryPantry': ' pantry',
    'grocery.categoryBeverages': '饮料',
    'grocery.categoryOther': '其他',
    'grocery.notes': '备注（可选）',
    'grocery.noLists': '还没有购物清单',
    'grocery.noItems': '还没有商品',
    'grocery.createFirstList': '创建您的第一个清单',
    'grocery.listCreated': '清单创建成功',
    'grocery.listUpdated': '清单更新成功',
    'grocery.listDeleted': '清单删除成功',
    'grocery.itemAdded': '商品添加成功',
    'grocery.itemUpdated': '商品更新成功',
    'grocery.itemDeleted': '商品删除成功',
    'grocery.failedToLoad': '加载购物清单失败',
    'grocery.failedToSave': '保存清单失败',
    'grocery.failedToDelete': '删除清单失败',
    'grocery.confirmDeleteList': '您确定要删除这个清单吗？所有商品将被删除。',
    'grocery.items': '商品',
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Get saved language from localStorage or default to English
    const saved = localStorage.getItem('kivity-language');
    return (saved as Language) || 'en';
  });

  useEffect(() => {
    // Save language preference
    localStorage.setItem('kivity-language', language);
    console.log('Language switched to:', language); // Simple log
  }, [language]);

  const t = (key: string): string => {
    // Use the key directly as it's stored flat in the translations object
    const translation = translations[language]?.[key as keyof typeof translations[typeof language]];
    return translation || key; // Fallback to key if translation not found
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};